/**
 * Charter audio feature (order `FEATURE_ORDER.charterAudio` = 630, after M3's `reachAudio`; M5 spec §20, plan C19).
 * Reach mode only. Like M2's `m2audio` and M3's `reachAudio`, it never edits the audio feature: it takes the running
 * `engine` and `music` from the audio feature's default export (both null until the first gesture) and adds:
 * - **the bridge** (src/audio/charter/bridge.ts): the strategic client's system, relations and gate views →
 *   stingers (claim flip, war and peace, the gate's flicker, tremor, closure and reopening, a vote result) and the
 *   theme that should play;
 * - **the Isolated Era theme:** the score re-keyed to the `isolated` region (re-asserted after a relay transit
 *   re-keys it to the system's region) and the organ figures on the score's bars; the system's key comes back
 *   when Isolation ends;
 * - **the Descendants motif:** the throat drone, the hand percussion and the throat phrases on the score's bars
 *   (a Holdfast system, or a guardian ship within the score's tension range from story tier 3);
 * - **stingers** on the next beat of the score's bar grid (the cue itself when the score is not up), the
 *   BRAAAM-scale ones also queuing the score's BRAAAM.
 * Dev forcing: `force(cue)` and `forceTheme(theme)`, and `window.__charterAudio` in dev builds. `frame` allocates
 * only the Web Audio nodes of the notes it starts (as the score does).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import type { AudioEngine } from '../../audio/engine'
import type { MusicSystem } from '../../audio/music'
import { musicConfig } from '../../audio/music/musicSchema'
import { keyFor, type Key } from '../../audio/music/theory'
import { keyScoreTo } from '../../audio/reach/router'
import { clearPending, createCharterBridge, cueBraaam, observeGate, observeRelations, observeSystemView, takeStinger, themeOf, type CharterBridge } from '../../audio/charter/bridge'
import {
  barSeconds,
  CHARTER_CUES,
  charterMusic,
  CUE,
  scheduleDescendantsBar,
  scheduleIsolatedBar,
  scheduleStinger,
  startDrone,
  THEME,
  THEME_NAMES,
  type CharterCue,
  type CharterMusicConfig,
  type DroneHandle,
  type ThemeCode,
} from '../../audio/charter/cues'
import { GUARDIAN_TEAM_ID } from '../../charter/hostilityWriter'
import { logger } from '../../core/logger'
import type { StrategicClient } from '../../strategic/client'
import { strategicBoot } from '../../strategic-link/service'
import { Life, Pos64, Team } from '../../world/components'
import audioFeature from '../audio/feature'

/** Where the running audio lives: the audio feature (a fake in tests). */
export interface CharterAudioSource {
  readonly engine: Pick<AudioEngine, 'ac' | 'musicIn'> | null
  readonly music: Pick<MusicSystem, 'key' | 'stats' | 'stinger' | 'setRegion'> | null
}

/** The client views the bridge reads. */
export type CharterViewClient = Pick<StrategicClient, 'onSystemView' | 'onRelations' | 'onGate' | 'relations' | 'systemView'>

export interface CharterAudioFeature extends GameFeature {
  readonly bridge: CharterBridge
  /** The theme playing (THEME code). */
  readonly theme: ThemeCode
  /** Dev: plays a stinger on the next beat, past its cooldown and the gap. */
  force(cue: CharterCue): void
  /** Dev: pins a theme (-1 releases it to the bridge). */
  forceTheme(theme: ThemeCode | -1): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

declare global {
  interface Window {
    /** Dev builds: the Charter cues by hand (`cue('gateClosure')`, `theme('isolated')`). */
    __charterAudio?: { cues: readonly string[]; cue(name: string): void; theme(name: string): void; stats(): CharterBridge['stats'] }
  }
}

export function createCharterAudioFeature(
  source: CharterAudioSource = audioFeature,
  clientOf: () => CharterViewClient | null = () => strategicBoot()?.client ?? null,
  cfg: CharterMusicConfig = charterMusic,
): CharterAudioFeature {
  const bridge = createCharterBridge()
  const fallbackKey: Key = keyFor(musicConfig, musicConfig.region, musicConfig.seed)
  const unsub: (() => void)[] = []
  let active = false
  let client: CharterViewClient | null = null
  /** Real seconds since init (the bridge's clock). */
  let clock = 0
  let theme: ThemeCode = THEME.none
  let forcedTheme: ThemeCode | -1 = -1
  let drone: DroneHandle | null = null
  let droneAc: BaseAudioContext | null = null
  /** The tonic the drone was started on (it restarts when the score changes key). */
  let droneRoot = -1
  let lastMusic: CharterAudioSource['music'] = null
  let lastBars = -1
  let bar = 0
  /** Audio time of the last bar start and the next bar of the own clock (no score). */
  let barAt = 0
  let nextOwnBar = 0
  let lastSystem = -1
  let guardianTimer = 0

  function bind(c: CharterViewClient): void {
    client = c
    if (c.relations !== null) observeRelations(bridge, c.relations, clock, cfg)
    if (c.systemView !== null) observeSystemView(bridge, c.systemView, clock, cfg)
    unsub.push(
      c.onSystemView((v) => observeSystemView(bridge, v, clock, cfg)),
      c.onRelations((v) => observeRelations(bridge, v, clock, cfg)),
      c.onGate((v) => observeGate(bridge, v, clock, cfg)),
    )
  }

  /** A guardian ship within the score's tension range of the player. Allocation-free. */
  function guardianNear(ctx: GameContext): boolean {
    const sim = ctx.sim
    const p = sim.playerEid
    if (p < 0 || Life.alive[p] !== 1 || GUARDIAN_TEAM_ID < 0) return false
    const r2 = musicConfig.intensity.tensionRange * musicConfig.intensity.tensionRange
    const ships = sim.ships.dense
    for (let i = 0; i < ships.length; i++) {
      const eid = ships[i]
      if (Team.id[eid] !== GUARDIAN_TEAM_ID || Life.alive[eid] !== 1) continue
      const dx = Pos64.x[eid] - Pos64.x[p]
      const dy = Pos64.y[eid] - Pos64.y[p]
      const dz = Pos64.z[eid] - Pos64.z[p]
      if (dx * dx + dy * dy + dz * dz <= r2) return true
    }
    return false
  }

  function releaseDrone(t: number): void {
    if (drone !== null) drone.release(t)
    drone = null
    droneAc = null
  }

  /** Re-keys the score for the theme (isolated) or back to the system (leaving it). Rare: may allocate. */
  function keyScore(ctx: GameContext, m: NonNullable<CharterAudioSource['music']>, isolated: boolean): void {
    if (m.setRegion === undefined) return
    if (isolated) {
      m.setRegion(cfg.isolated.region, musicConfig.seed)
      return
    }
    const core = ctx.reach
    const layout = core?.session.layout ?? null
    if (core !== null && layout !== null) keyScoreTo(core, layout.systemIndex, m)
  }

  const feature: CharterAudioFeature = {
    name: 'charterAudio',
    order: FEATURE_ORDER.charterAudio,
    bridge,
    get theme(): ThemeCode {
      return theme
    },

    init(ctx: GameContext): void {
      if (ctx.reach === null) return
      active = true
      if (import.meta.env.DEV && typeof window !== 'undefined') {
        window.__charterAudio = {
          cues: CHARTER_CUES,
          cue(name: string): void {
            const c = CHARTER_CUES.find((x) => x === name)
            if (c === undefined) logger.warn(`charter audio: unknown cue ${name}`)
            else feature.force(c)
          },
          theme(name: string): void {
            const i = THEME_NAMES.findIndex((x) => x === name)
            feature.forceTheme(i < 0 ? -1 : (i as ThemeCode))
          },
          stats: () => ({ ...bridge.stats }),
        }
      }
    },

    force(cue: CharterCue): void {
      bridge.pending = CUE[cue]
      bridge.lastPlayAt = -Infinity
    },

    forceTheme(t: ThemeCode | -1): void {
      forcedTheme = t
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      if (!active) return
      clock += f.dt
      if (client === null) {
        const c = clientOf()
        if (c !== null) bind(c)
      }
      if (bridge.tier3) {
        guardianTimer -= f.dt
        if (guardianTimer <= 0) {
          guardianTimer = 1 / cfg.guardianCheckHz
          bridge.guardianNear = guardianNear(ctx)
        }
      } else bridge.guardianNear = false

      const e = source.engine
      if (e === null || f.paused) {
        clearPending(bridge)
        return
      }
      const ac = e.ac
      if (droneAc !== null && droneAc !== ac) releaseDrone(0)
      const m = source.music
      const key = m !== null ? m.key : fallbackKey
      const la = cfg.lookaheadSeconds
      const now = ac.currentTime + la

      // Theme changes (and the score's key for the Isolated Era).
      const want: ThemeCode = forcedTheme === -1 ? themeOf(bridge) : forcedTheme
      const layout = ctx.reach?.session.layout ?? null
      const sys = layout === null ? -1 : layout.systemIndex
      if (want !== theme || m !== lastMusic || (want === THEME.isolated && sys !== lastSystem)) {
        const wasIsolated = theme === THEME.isolated
        const isolated = want === THEME.isolated
        // Isolated: key the score on entry, on a new score, and after a transit re-keyed it to the system's region.
        if (m !== null && isolated) keyScore(ctx, m, true)
        else if (m !== null && wasIsolated && m === lastMusic) keyScore(ctx, m, false)
        if (want !== THEME.descendants) releaseDrone(now)
        theme = want
        lastMusic = m
      }
      if (theme === THEME.descendants && (drone === null || key.root !== droneRoot)) {
        releaseDrone(now)
        drone = startDrone(ac, e.musicIn, key, now, cfg)
        droneAc = ac
        droneRoot = key.root
      }
      lastSystem = sys

      // The bar grid: the score's bars, or an own clock at the fallback tempo while the score is not up.
      let tick = false
      if (m !== null) {
        const bars = m.stats.bars
        if (bars !== lastBars) {
          lastBars = bars
          barAt = now
          tick = true
        }
      } else if (now >= nextOwnBar) {
        barAt = Math.max(nextOwnBar, now)
        nextOwnBar = barAt + barSeconds(key)
        tick = true
      }
      if (tick) {
        if (theme === THEME.descendants) scheduleDescendantsBar(ac, e.musicIn, bar, key, barAt, drone, cfg)
        else if (theme === THEME.isolated) scheduleIsolatedBar(ac, e.musicIn, bar, key, barAt, cfg)
        bar++
      }

      // Stingers, on the next beat of the bar grid.
      const cue = takeStinger(bridge, clock, cfg)
      if (cue >= 0) {
        const beat = barSeconds(key) / 4
        const t = barAt + Math.ceil(Math.max(0, now - barAt) / beat) * beat
        scheduleStinger(ac, e.musicIn, CHARTER_CUES[cue], key, t, cfg)
        if (m !== null && cueBraaam(cue, cfg)) m.stinger('braaam')
      }
    },

    dispose(): void {
      for (const u of unsub) u()
      unsub.length = 0
      releaseDrone(droneAc === null ? 0 : droneAc.currentTime)
      client = null
      active = false
      theme = THEME.none
      lastMusic = null
      if (typeof window !== 'undefined' && window.__charterAudio !== undefined) window.__charterAudio = undefined
    },
  }
  return feature
}

const feature: CharterAudioFeature = createCharterAudioFeature()

export default feature
